'use client'
import { useAtom } from 'jotai'
import { useCallback, useMemo, useState } from 'react'
import {
  Line,
  LineChart,
  ReferenceLine as ReReferenceLine,
  XAxis,
  YAxis,
} from 'recharts'
import type { CategoricalChartState } from 'recharts/types/chart/types'
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
} from '@/src/components/ui/chart'
import {
  RadioButtonGroup,
  RadioButtonGroupItem,
} from '@/src/components/ui/radio-button-group'
import { TooltipContainer } from '@/src/components/ui/tooltip'
import { useAmplitudeAnalytics } from '@/src/hooks/use-amplitude-analytics'
import type { useTokenInfoMarketChartData } from '@/src/queries/hooks/token-info/asset-market-chart'
import {
  priceChartTimeIntervalAtom,
  TIME_INTERVALS,
} from '@/src/stores/token-pages/atoms'
import { PriceDisplay } from '../../price-display'
import { ChartSkeleton } from './skeleton'

function expandDomain(domain: number[], ratio = 0.1, side = 'both') {
  const min = domain[0]
  const max = domain[1]
  const offset = (max - min) * ratio
  const offsetLeft = side === 'both' || side === 'left' ? offset : 0
  const offsetRight = side === 'both' || side === 'right' ? offset : 0
  return [min - offsetLeft, max + offsetRight]
}

function formatPriceDate(
  timestamp: number,
  oldestPrice: number,
  newestPrice: number,
) {
  const firstDate = new Date(oldestPrice)
  const lastDate = new Date(newestPrice)

  if (firstDate.getFullYear() === lastDate.getFullYear())
    return new Date(timestamp).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
    })

  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  })
}

type DataPoint = {
  price: number
  time: string
} | null

export type ChartHoverData =
  | (DataPoint & {
      firstPriceInSequence: number
    })
  | null

interface ChartProps {
  chartData?: ReturnType<typeof useTokenInfoMarketChartData>['data']
  ticker: string
  isLoadingChartData?: boolean
  focusedPoint?: DataPoint
  onChartHover?: (data: ChartHoverData) => void
}

export default function Chart({
  chartData,
  ticker,
  isLoadingChartData,
  focusedPoint,
  onChartHover,
}: ChartProps) {
  const [isHovered, setIsHovered] = useState(false)
  const handleMouseMove = useCallback(
    (e: CategoricalChartState) => {
      setIsHovered(true)
      if (!e?.activePayload?.[0]) {
        onChartHover?.(null)
        return
      }

      onChartHover?.({
        ...e?.activePayload[0].payload,
        firstPriceInSequence: chartData?.prices[0].price,
      })
    },
    [chartData?.prices, onChartHover],
  )

  const chartConfig = useMemo(
    () =>
      ({
        price: {
          label: ticker,
        },
      }) satisfies ChartConfig,
    [ticker],
  )

  const oldestChartPoint = chartData?.prices[0]!.timestamp!
  const newestChartPoint = chartData?.prices.at(-1)!.timestamp!

  const highestPriceIndex = chartData?.prices.reduce(
    (acc, curr, i) =>
      acc.price > curr.price
        ? acc
        : {
            price: curr.price,
            index: i,
          },
    {
      price: Number.NEGATIVE_INFINITY,
      index: -1,
    },
  ).index

  const highestPrice =
    highestPriceIndex !== undefined && highestPriceIndex >= 0
      ? chartData?.prices[highestPriceIndex].price
      : undefined
  const lowestPriceIndex = chartData?.prices.reduce(
    (acc, curr, i) =>
      acc.price < curr.price
        ? acc
        : {
            price: curr.price,
            index: i,
          },
    {
      price: Number.POSITIVE_INFINITY,
      index: -1,
    },
  ).index
  const lowestPrice =
    lowestPriceIndex !== undefined && lowestPriceIndex >= 0
      ? chartData?.prices[lowestPriceIndex].price
      : undefined

  const interactableDataPoints = useMemo(
    () =>
      chartData?.prices.filter(
        (_, i) =>
          (Math.abs(i - highestPriceIndex!) > 2 &&
            Math.abs(i - lowestPriceIndex!) > 2) ||
          i === highestPriceIndex ||
          i === lowestPriceIndex,
      ),
    [chartData, highestPriceIndex, lowestPriceIndex],
  )

  const estimatedMargin = useEstimatedMargin(lowestPrice, highestPrice)
  const chartHeightClasses = 'h-[12rem] sm:h-[20rem] 2xl:h-[28rem]'

  if (isLoadingChartData)
    return (
      <div className='relative flex flex-col gap-4 w-full py-3'>
        <ChartSkeleton className={chartHeightClasses} />
        <Footer />
      </div>
    )

  return (
    <div className='relative flex flex-col gap-4 w-full py-3'>
      <ChartContainer
        config={chartConfig}
        className={`${chartHeightClasses} text-tertiary text-xs`}
      >
        {chartData ? (
          <LineChart
            data={interactableDataPoints}
            key='chart'
            dataKey='price'
            onMouseMove={handleMouseMove}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => {
              onChartHover?.(null)
              setIsHovered(false)
            }}
            margin={{
              top: 12,
              left: 0,
              right: estimatedMargin,
              bottom: 6,
            }}
          >
            <YAxis
              dataKey={'price'}
              domain={expandDomain([chartData.minValue, chartData.maxValue])}
              interval='equidistantPreserveStart'
              orientation='right'
              axisLine={false}
              tickLine={false}
              tickCount={16}
              mirror={true}
              tick={<CustomYAxisTick />}
            />
            <XAxis
              dataKey={'timestamp'}
              type='number'
              domain={expandDomain([oldestChartPoint, newestChartPoint], 0.03)}
              tickLine={false}
              axisLine={false}
              tickCount={6}
              interval='equidistantPreserveStart'
              tickFormatter={(value) => {
                return formatPriceDate(
                  value,
                  oldestChartPoint,
                  newestChartPoint,
                )
              }}
              tick={CustomXAxisTick}
            />
            <Line
              dataKey='price'
              type='natural'
              stroke='var(--primary)'
              strokeWidth={2}
              activeDot={
                <LowestHighestDots
                  isActive={true}
                  isHovered={isHovered}
                  highestValue={highestPrice}
                  lowestValue={lowestPrice}
                />
              }
              dot={
                <LowestHighestDots
                  isHovered={isHovered}
                  highestValue={highestPrice}
                  lowestValue={lowestPrice}
                />
              }
              animationDuration={200}
            />
            <ReferenceLine referencePoint={focusedPoint} />
            <ChartTooltip
              isAnimationActive={false}
              content={({ payload }) => {
                if (payload?.length) {
                  const price = payload[0].payload.price
                  if (price === highestPrice)
                    return <TooltipContainer>Highest</TooltipContainer>
                  if (price === lowestPrice)
                    return <TooltipContainer>Lowest</TooltipContainer>
                }
              }}
              cursor={{ stroke: 'var(--border2)' }}
            />
          </LineChart>
        ) : (
          <NoData />
        )}
      </ChartContainer>
      <Footer />
    </div>
  )
}

function numberOfFractionDigits(price: number) {
  if (price > 1) return 2
  if (price > 0.0001) return 4
  return 8
}

function useEstimatedMargin(min?: number, max?: number) {
  return useMemo(() => {
    if (min == null || max == null) return 0

    // worst-case label comes from the larger magnitude
    const maxAbs = Math.max(Math.abs(min), Math.abs(max))

    // integer digits (assume at least 1 for 0.x)
    const intDigits = maxAbs >= 1 ? Math.floor(Math.log10(maxAbs)) + 1 : 1

    // fraction digits per your rule
    const fracDigits = numberOfFractionDigits(maxAbs)

    // thousands separators for en-US style
    const thousands = intDigits > 3 ? Math.floor((intDigits - 1) / 3) : 0

    // reserve a minus sign if the range crosses below zero
    const hasNegative = min < 0

    // punctuation count: thousands + decimal point (if any) + minus sign (if any)
    const punctuation =
      thousands + (fracDigits > 0 ? 1 : 0) + (hasNegative ? 1 : 0)

    return (intDigits + fracDigits) * 7.5 + punctuation * 1
  }, [min, max])
}

function NoData() {
  return (
    <div className='relative w-full h-full pb-8 flex items-end'>
      <div className='flex items-center justify-center absolute inset-0'>
        <span className='text-tertiary text-base'>
          No historical price data available
        </span>
      </div>
      <div className='w-full h-0.5 bg-border1' />
    </div>
  )
}

const CustomXAxisTick = ({
  x,
  y,
  className,
  index,
  visibleTicksCount,
  tickFormatter,
  payload,
  // biome-ignore lint/suspicious/noExplicitAny: As per Recharts documentation
}: any) => {
  const anchor =
    index === 0 ? 'start' : index === visibleTicksCount - 1 ? 'end' : 'middle'

  return (
    <g transform={`translate(${x}, ${y + 23})`} className={className}>
      <text
        x={0}
        y={0}
        fontSize={12}
        textAnchor={anchor}
        fill='var(--tertiary)'
      >
        {tickFormatter(payload.value, index)}
      </text>
    </g>
  )
}

// biome-ignore lint/suspicious/noExplicitAny: As per Recharts documentation
const CustomYAxisTick = ({ x, y, payload }: any) => {
  return (
    <g transform={`translate(${x}, ${y})`}>
      <text fontSize={12} textAnchor='start' fill='var(--tertiary)'>
        <PriceDisplay price={payload.value} />
      </text>
    </g>
  )
}

// biome-ignore lint/suspicious/noExplicitAny: As per Recharts documentation
const LowestHighestDots = (props: any) => {
  const { cx, cy, value, isActive, isHovered, highestValue, lowestValue } =
    props

  if (!isHovered) return null

  if (value === lowestValue) {
    return (
      <circle
        cx={cx}
        cy={cy}
        r={5}
        fill='var(--danger-text)'
        strokeWidth={2}
        stroke='var(--background)'
      />
    )
  }

  if (value === highestValue) {
    return (
      <circle
        cx={cx}
        cy={cy}
        r={5}
        fill='var(--success-text)'
        strokeWidth={2}
        stroke='var(--background)'
      />
    )
  }

  if (isActive) {
    return (
      <circle
        cx={cx}
        cy={cy}
        r={5}
        fill='var(--accent-primary)'
        strokeWidth={2}
        stroke='var(--background)'
      />
    )
  }

  return null
}

const ReferenceLine = ({ referencePoint }: { referencePoint?: DataPoint }) => {
  if (!referencePoint) return

  return <ReReferenceLine y={referencePoint.price} stroke='var(--border2)' />
}

const Footer = () => {
  const { ampli } = useAmplitudeAnalytics()
  const [selectedTimeInterval, setSelectedTimeInterval] = useAtom(
    priceChartTimeIntervalAtom,
  )
  return (
    <div>
      <RadioButtonGroup
        onValueChange={(value) => {
          const timeInterval = TIME_INTERVALS.find(
            ({ value: v }) => v === value,
          )

          if (timeInterval) {
            setSelectedTimeInterval(timeInterval)

            ampli?.pageInteraction({
              page: 'token info',
              key: 'click on timespan button filter',
              context: timeInterval.label,
            })
          }
        }}
        value={selectedTimeInterval?.value}
      >
        {TIME_INTERVALS.map(({ label, value }) => (
          <RadioButtonGroupItem key={value} value={value}>
            {label}
          </RadioButtonGroupItem>
        ))}
      </RadioButtonGroup>
    </div>
  )
}
